import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, ArrowRight, MapPin, GraduationCap, BookOpen, School } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../hooks/useAuth';

const CollegeDatabase: React.FC = () => {
  const [colleges, setColleges] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    stream: '',
    city: '',
    type: ''
  });
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const pageHeight = window.innerHeight;
      const currentScroll = window.scrollY;
      const scrollCount = Math.floor(currentScroll / pageHeight);

      if (scrollCount >= 2 && !user && !showSignUpPrompt) {
        setShowSignUpPrompt(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [user, showSignUpPrompt]);

  useEffect(() => {
    const fetchColleges = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('colleges')
          .select('*')
          .order('name')
          .limit(user ? 1000 : 30);

        if (error) throw error;
        setColleges(data || []);
      } catch (error) {
        console.error('Error fetching colleges:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchColleges();
  }, [user]);

  const filteredColleges = colleges.filter(college =>
    (college.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    college.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    college.stream?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!selectedFilters.stream || college.stream === selectedFilters.stream) &&
    (!selectedFilters.city || college.city === selectedFilters.city) &&
    (!selectedFilters.type || college.college_type === selectedFilters.type)
  );

  const uniqueStreams = Array.from(new Set(colleges.map(c => c.stream))).filter(Boolean);
  const uniqueCities = Array.from(new Set(colleges.map(c => c.city))).filter(Boolean);
  const uniqueTypes = Array.from(new Set(colleges.map(c => c.college_type))).filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">College Database</h1>
                <p className="text-gray-600">
                  Browse and compare colleges across Maharashtra
                </p>
              </div>
              
              <div className="mt-4 md:mt-0">
                <Link 
                  to="/register" 
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#7B4AE2] hover:bg-[#6A3ACF]"
                >
                  Sign Up for Full Access
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search colleges..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7B4AE2] focus:border-transparent"
                  />
                </div>

                <select
                  value={selectedFilters.stream}
                  onChange={(e) => setSelectedFilters(prev => ({ ...prev, stream: e.target.value }))}
                  className="block w-full rounded-lg border-gray-300 focus:ring-2 focus:ring-[#7B4AE2] focus:border-transparent"
                >
                  <option value="">All Streams</option>
                  {uniqueStreams.map(stream => (
                    <option key={stream} value={stream}>{stream}</option>
                  ))}
                </select>

                <select
                  value={selectedFilters.city}
                  onChange={(e) => setSelectedFilters(prev => ({ ...prev, city: e.target.value }))}
                  className="block w-full rounded-lg border-gray-300 focus:ring-2 focus:ring-[#7B4AE2] focus:border-transparent"
                >
                  <option value="">All Cities</option>
                  {uniqueCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>

                <select
                  value={selectedFilters.type}
                  onChange={(e) => setSelectedFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="block w-full rounded-lg border-gray-300 focus:ring-2 focus:ring-[#7B4AE2] focus:border-transparent"
                >
                  <option value="">All Types</option>
                  {uniqueTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B4AE2]"></div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            College Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stream
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            City
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ranking
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredColleges.map((college) => (
                          <tr key={college.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-[#F0E9FF] rounded-lg flex items-center justify-center">
                                  <School className="h-5 w-5 text-[#7B4AE2]" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{college.name}</div>
                                  {college.website && (
                                    <a 
                                      href={college.website_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-[#7B4AE2] hover:underline"
                                    >
                                      Visit Website
                                    </a>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {college.stream}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-500">
                                <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                                {college.city}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {college.college_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {college.overall_rank ? (
                                <div className="text-sm font-medium text-gray-900">
                                  #{college.overall_rank}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500">Not ranked</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {!user && (
                    <div className="mt-8 bg-[#F0E9FF] rounded-lg p-6 text-center">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Access Complete Database
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Sign up to view our complete database of colleges with detailed information and features.
                      </p>
                      <Link
                        to="/register"
                        className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
                      >
                        Sign Up Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {showSignUpPrompt && !user && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transform transition-transform duration-500 ease-in-out translate-y-0 z-50">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-gray-900">
                  Want to see more colleges?
                </h3>
                <p className="text-gray-600 mt-1">
                  Sign up to access our complete database of colleges and features
                </p>
              </div>
              <div className="flex gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
                >
                  Sign Up Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button
                  onClick={() => setShowSignUpPrompt(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CollegeDatabase;